import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import client from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, UploadCloud } from 'lucide-react';

export const DOC_CATEGORIES = [
  ['CDL', "Commercial Driver's License"], ['MEDICAL_CARD', 'DOT Medical Card'],
  ['INSURANCE', 'Proof of Insurance'], ['SSN', 'Social Security Number'],
  ['MVR', 'Motor Vehicle Record'], ['VEHICLE_REGISTRATION', 'Vehicle Registration'],
  ['IFTA', 'IFTA Permits'], ['DRUG_TEST', 'Pre-employment Drug Test'],
  ['CLEARINGHOUSE', 'Clearinghouse Query'], ['MRI_DRUG_TEST', 'MRI Drug Test'],
  ['ANNUAL', 'Annual Review'], ['OTHER', 'Other'],
];

export const TAG_OPTIONS = ['Driver', 'DQ', 'Medical Card', 'MVR', 'Pre-employment', 'Clearinghouse Query', 'Result-DT', 'PEV Report', 'Annual', 'Inspection'];

export function UploadDialog({ open, onOpenChange, driverId, defaultCategory, onUploaded, lockCategory }) {
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState(defaultCategory || 'CDL');
  const [expires, setExpires] = useState('');
  const [tags, setTags] = useState([]);
  const [powerUnit, setPowerUnit] = useState('none');
  const [replace, setReplace] = useState(true);
  const [units, setUnits] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [driver, setDriver] = useState(driverId || 'none');
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (defaultCategory) setCategory(defaultCategory); }, [defaultCategory, open]);
  useEffect(() => { setDriver(driverId || 'none'); }, [driverId, open]);
  useEffect(() => {
    if (!open) return;
    client.get('/vehicles?unit_type=power_unit').then((r) => setUnits(r.data)).catch(() => {});
    if (!driverId) client.get('/drivers?status=active').then((r) => setDrivers(r.data)).catch(() => {});
  }, [open, driverId]);

  const toggleTag = (t) => setTags((s) => s.includes(t) ? s.filter((x) => x !== t) : [...s, t]);

  const submit = async () => {
    if (!file) { toast.error('Please choose a file (PDF/JPG/PNG under 15MB)'); return; }
    setLoading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('category', category);
    const did = driverId || (driver !== 'none' ? driver : null);
    if (did) fd.append('driver_id', did);
    if (powerUnit !== 'none') fd.append('power_unit_id', powerUnit);
    if (tags.length) fd.append('tags', tags.join(','));
    if (expires) fd.append('expires_at', new Date(expires).toISOString());
    fd.append('replace', replace ? 'true' : 'false');
    try {
      await client.post('/documents/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded');
      setFile(null); setExpires(''); setTags([]); setPowerUnit('none');
      onUploaded && onUploaded();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally { setLoading(false); }
  };

  const showExpiry = ['CDL', 'MEDICAL_CARD', 'INSURANCE', 'VEHICLE_REGISTRATION', 'IFTA'].includes(category);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="upload-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Upload Document</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {!lockCategory && (
            <div>
              <Label>Document Type</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="mt-1" data-testid="upload-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_CATEGORIES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 text-center transition-colors hover:bg-muted" data-testid="upload-dropzone">
            <UploadCloud className="h-7 w-7 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">{file ? file.name : 'Drag & drop files or browse (PDF, JPG, PNG · max 15MB)'}</span>
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="upload-file-input" />
          </label>

          {!driverId && (
            <div>
              <Label>Assign to Driver</Label>
              <Select value={driver} onValueChange={setDriver}>
                <SelectTrigger className="mt-1" data-testid="upload-driver-select"><SelectValue placeholder="Select driver..." /></SelectTrigger>
                <SelectContent><SelectItem value="none">— None —</SelectItem>{drivers.map((d) => <SelectItem key={d.driver_id} value={d.driver_id}>{d.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label>Assign to Power Unit</Label>
            <Select value={powerUnit} onValueChange={setPowerUnit}>
              <SelectTrigger className="mt-1" data-testid="upload-power-unit-select"><SelectValue placeholder="Select vehicle unit..." /></SelectTrigger>
              <SelectContent><SelectItem value="none">— None —</SelectItem>{units.map((v) => <SelectItem key={v.vehicle_id} value={v.vehicle_id}>#{v.unit_number} · {v.make} {v.model}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div>
            <Label>Document Tags</Label>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {TAG_OPTIONS.map((t) => (
                <button key={t} type="button" onClick={() => toggleTag(t)} data-testid={`upload-tag-${t.toLowerCase().replace(/[^a-z]+/g,'-')}`}
                  className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${tags.includes(t) ? 'bg-[hsl(var(--primary))] text-white' : 'border bg-white text-muted-foreground hover:bg-muted'}`}>{t}</button>
              ))}
            </div>
          </div>

          {showExpiry && (
            <div><Label>Expiration Date</Label><Input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} className="mt-1" data-testid="upload-expiry-input" /></div>
          )}
          <div className="flex items-center gap-2 rounded-lg border bg-muted/40 p-3">
            <Switch checked={replace} onCheckedChange={setReplace} data-testid="upload-replace-switch" />
            <span className="text-sm">Archive previous version of this document type</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={loading} data-testid="upload-submit-button">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Upload</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
